/* eslint-disable camelcase */
import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
//
import { dispatch } from '../store';
import conObj from '../../utils/connection-assistant';
import { getFingerprint, getLocalId } from '../../utils/formatObject';

// ----------------------------------------------------------------------

const initialState = {
  inbox: [],
  unreadInboxCount: 0,
  memberBalance: {
    currencyCode: '',
    amount: 0,
    balance: 0,
    displayBalance: 0,
  },
  memberBankInfo: {
    bank: '',
    bankAccountNumber: '',
  },
  memberBanks: [],
  addBankLimit: 0,
  gameCategsAndCompanies: [],
  gameCategs: [],
  siteSettings: JSON.parse(localStorage.getItem('site_configs') || '{}') || {},
  promotions: { data: [], categories: [] },
  angPauPromos: { data: [], isFetchedOnce: false },
  withdrawRequirement: {
    calculationType: '',
    current: 0,
    requirement: 0,
    isSufficient: false,
    allowWithdraw: false,
  },
  depositWithdrawMinMax: {
    maxWithdrawLimit: 0,
    minWithdrawLimit: 0,
    currencyWithdraw: {},
    currencyTopup: {},
  },
  userProfile: JSON.parse(localStorage.getItem('userProfile') || '{}') || {},
  transactionStats: [],
  sportsMarquee: '',
};

const slice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    setMemberBalance(state, action) {
      state.memberBalance = action.payload;
    },
    setInbox(state, action) {
      state.inbox = action.payload;
    },
    setUnreadInboxCount(state, action) {
      state.unreadInboxCount = action.payload;
    },
    setGameCategsAndCompanies(state, action) {
      state.gameCategsAndCompanies = action.payload;
    },
    setGameCategs(state, action) {
      state.gameCategs = action.payload;
    },
    setSiteSettings(state, action) {
      state.siteSettings = action.payload;
    },
    setPromotions(state, action) {
      state.promotions = action.payload;
    },
    claimPromotion(state, action) {
      const newPromotions = JSON.parse(JSON.stringify(state.promotions?.data));

      if (newPromotions?.length) {
        state.promotions = {
          ...state.promotions,
          data: newPromotions.map((x) => {
            if (action.payload === x?.id) {
              return { ...x, is_claimable_now: false };
            }

            return x;
          }),
        };
      }
    },
    claimPromotionBanner(state, action) {
      const newBanner = JSON.parse(JSON.stringify(state.siteSettings?.sliders));

      if (newBanner?.length) {
        state.siteSettings = {
          ...state.siteSettings,
          sliders: newBanner.map((x) => {
            if (action.payload === x?.BannerSlide?.id) {
              return {
                BannerSlide: {
                  ...x?.BannerSlide,
                  is_claimable_now: false,
                },
              };
            }

            return x;
          }),
        };
      }
    },
    setAngPauPromos(state, action) {
      state.angPauPromos = action.payload;
    },
    setFakePlayerStats(state, action) {
      state.fakePlayerStats = action.payload;
    },
    setTransactionStats(state, action) {
      state.transactionStats = action.payload;
    },
    setWithdrawRequirement(state, action) {
      state.withdrawRequirement = action.payload;
    },
    setMemberBankInfo(state, action) {
      state.memberBankInfo = action.payload;
    },
    setMemberBanks(state, action) {
      state.memberBanks = action.payload;
    },
    setAddBankLimit(state, action) {
      state.addBankLimit = action.payload;
    },
    setDepositWithdrawMinMax(state, action) {
      state.depositWithdrawMinMax = action.payload;
    },
    setUserProfile(state, action) {
      state.userProfile = action.payload;
    },
    setSportsMarquee(state, action) {
      state.sportsMarquee = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setMemberBalance,
  setInbox,
  setUnreadInboxCount,
  setGameCategsAndCompanies,
  setGameCategs,
  setSiteSettings,
  setPromotions,
  claimPromotion,
  claimPromotionBanner,
  setAngPauPromos,
  setFakePlayerStats,
  setTransactionStats,
  setWithdrawRequirement,
  setMemberBankInfo,
  setMemberBanks,
  setAddBankLimit,
  setDepositWithdrawMinMax,
  setUserProfile,
  setSportsMarquee,
} = slice.actions;

const siteSettingCallback = (data) => {
  let _popupBanner = [];

  if (data?.popups.length > 0) {
    _popupBanner = data?.popups.map((x) => ({
      showPopup: true,
      ...x?.BannerSlide,
    }));
  }

  const _siteSettings = {
    ...data?.configs,
    announcement_text: data?.announcement_text,
    sliders: data?.sliders,
    languages: data?.languages,
    memberGroupBanners: data?.member_group_banners,
    referralBanners: data?.referral_banners,
    popups: _popupBanner,
  };
  dispatch(setSiteSettings(_siteSettings));

  localStorage.setItem('site_configs', JSON.stringify(_siteSettings));
};

export async function callMultipleRequests() {
  const requestData = {
    'requests[0]': JSON.stringify({ url: 'setting/site_configs.json', extra_input: {} }),
  };

  const isAuth = localStorage.getItem('accessToken');

  const { fpResult, visitorId } = await getFingerprint();
  const localId = await getLocalId();

  if (isAuth) {
    requestData['requests[1]'] = JSON.stringify({ url: 'members/balance.json', extra_input: {} });
    requestData['requests[2]'] = JSON.stringify({ url: 'profile.json', extra_input: {} });
  }
  
  requestData.fingerprint = visitorId;
  requestData.fingerprint_data = fpResult;
  requestData.local_id = localId;

  const response = await conObj.post('multi.json', requestData);

  if (response.status === 200) {
    const multiResponse = response.data?.data?.multiResults;

    const siteSettingResponse = multiResponse['setting/site_configs.json'];
    if (siteSettingResponse) {
      siteSettingCallback(siteSettingResponse?.data?.data);
    }

    if (isAuth) {
      const balanceResponse = multiResponse['members/balance.json'];

      const { data: balance, withdrawRequirement } = balanceResponse?.data;
      dispatch(
        setMemberBalance({
          currencyCode: balance?.currency,
          balance: balance?.balance,
          displayBalance: balance?.display_balance,
          canClearBalance: balance?.can_clear_balance,
        })
      );
      dispatch(setWithdrawRequirement(withdrawRequirement));

      const profileResponse = multiResponse['profile.json'];
      dispatch(setUserProfile(profileResponse?.data?.data));
      localStorage.setItem('userProfile', JSON.stringify(profileResponse?.data?.data));
    }
  }

  return response;
}

export async function getUserMemberBalance() {
  const { fpResult, visitorId } = await getFingerprint();
  const localId = await getLocalId();

  const response = await conObj.post('members/balance.json', {
    fingerprint: visitorId,
    fingerprint_data: fpResult,
    local_id: localId,
  });

  const { data: balance, profile } = response.data?.data;

  dispatch(
    setMemberBalance({
      currencyCode: balance?.currency,
      balance: balance?.balance,
      displayBalance: balance?.display_balance,
      canClearBalance: balance?.can_clear_balance,
    })
  );
  dispatch(setWithdrawRequirement(response.data?.data?.withdrawRequirement));

  dispatch(setUserProfile(profile));

  return response;
}

export function getUserBanks() {
  return conObj.post('members/banks.json').then((response) => {
    const {
      balance,
      data: _memberBank,
      maxWithdrawLimit,
      minWithdrawLimit,
      currencyTopup,
      currencyWithdraw,
      profile,
      maxBankAccountCount,
    } = response.data?.data;

    dispatch(
      setMemberBalance({
        currencyCode: balance?.currency,
        balance: balance?.balance,
        displayBalance: balance?.display_balance,
        canClearBalance: balance?.can_clear_balance,
      })
    );
    dispatch(setWithdrawRequirement(response.data?.data?.withdrawRequirement));
    dispatch(setMemberBanks(_memberBank));
    dispatch(setAddBankLimit(Number(maxBankAccountCount || 0)));
    if (_memberBank?.length > 0) {
      dispatch(
        setMemberBankInfo({
          bank: _memberBank?.[0]?.Bank?.name,
          bankAccountNumber: _memberBank?.[0]?.account_number,
        })
      );
      dispatch(
        setDepositWithdrawMinMax({
          maxWithdrawLimit,
          minWithdrawLimit,
          currencyTopup,
          currencyWithdraw,
        })
      );
    }

    dispatch(setUserProfile(profile));
  });
}

export function getInbox(page = 0, limit = 20) {
  return conObj.post(`notifications.json?page=${page}&limit=${limit}`).then((response) => {
    dispatch(setInbox(response.data.data.data));
  });
}

export function getUnreadInboxCount(page = 0, limit = 20) {
  return conObj.post(`unread_notifications.json?page=${page}&limit=${limit}`).then((response) => {
    console.log('unread here');
    dispatch(setUnreadInboxCount(response.data.data.data.count));
  });
}

export function getGameCategsAndCompanies() {
  return conObj.post('games/game_categories.json').then((response) => {
    dispatch(setGameCategsAndCompanies(response?.data?.data?.data));
  });
}

export function getGameCategs() {
  return conObj.post('games/categories.json').then((response) => {
    dispatch(setGameCategs(response?.data?.data?.data));
  });
}

export function getSiteSettings() {
  console.log('getting site settings');
  return conObj.post('setting/site_configs.json').then((response) => siteSettingCallback(response?.data?.data?.data));
}

export function getPromotions() {
  return conObj.post('promotion.json').then((response) => {
    dispatch(
      setPromotions({
        data: response.data?.data?.data,
        categories: response.data?.data?.categories,
        groupings: response.data?.data?.groupings,
      })
    );
  });
}

export function getAngPauPromotion() {
  return conObj
    .post('promotion.json', {
      promotion_type: 'angpau_manual',
    })
    .then((response) => {
      const respData = response.data?.data?.data;
      if (respData) dispatch(setAngPauPromos({ data: respData, isFetchedOnce: true }));
    });
}

export function getFakePlayerStats() {
  return conObj.post('setting/player_stats.json').then((response) => {
    if (response.data?.data?.data) {
      dispatch(setFakePlayerStats(response?.data?.data?.data?.latest_player_wins));
    }
  });
}

export function getTransactionStats() {
  return conObj.post('setting/transaction_stats.json').then((response) => {
    if (response.data?.data?.data) {
      dispatch(setTransactionStats(response?.data?.data?.data));
    }
  });
}
